import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { clone } from 'underscore';
import { PhotoDetailConstants as ui } from '../../Config/UIConstants';
import { TagType } from '../../Services/PhotoService';
import { IStoryFromServer } from '../../Services/StoryService';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { CommonTabbedContainer, Tab } from '../../SharedComponents/CommonTabbedContainer';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { IUserPhoto } from '../../Stores/UserPhotoStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { colors } from '../../styles/colors';
import { getNextRotation, rotateImage } from '../../util/resizeImage';
import { PhotoDataInput } from '../AddPhotosPage/PhotoDataInput';

interface IState {
    activeTab: Tab;
    photo?: IUserPhoto;
    photoIndex: number;
    rotatedPhoto: Blob | undefined;
    rotatedThumbnail: Blob | undefined;
    storiesForPhoto: Array<IStoryFromServer>;
    showRemovePopup: boolean;
    removeStoryIndex: number | undefined;
    newRotation: number;
    mustRemoveFromStory: boolean;
    onPressExit(didSave: boolean): void;
    isSavingPhoto: boolean;
    originalDate?: string;
    originalLocation?: string;
}

interface IProps {
    store: ApplicationStore;
}

@inject('store')
@observer
export class PhotoDetail extends React.Component<RouteComponentProps<any> & IProps> {
    public state: IState = {
        activeTab: Tab.LEFT,
        storiesForPhoto: [],
        showRemovePopup: false,
        removeStoryIndex: undefined,
        newRotation: 0,
        mustRemoveFromStory: false,
        rotatedPhoto: undefined,
        rotatedThumbnail: undefined,
        isSavingPhoto: false,
        originalLocation: undefined,
        originalDate: undefined,
        photo: undefined,
        photoIndex: -1,
        onPressExit: (didSave: boolean) => this.defaultOnPressExit(didSave),
    };

    private originalPhotoURL = '';
    private originalThumbnailURL: string | undefined;
    private hasRotated = false;

    private defaultOnPressExit = (didSave: boolean): void => {
        if (didSave === false) {
            const photo = this.props.store.userPhotoStore!.userPhotos.find(x => x.id === parseInt(this.props.match.params.id));
            if (photo) {
                photo.url = this.originalPhotoURL;
            }
        }

        if (this.props.location.state.cameFromStory) {
            this.props.history.goBack();
        } else {
            this.props.history.push(Routes.PHOTOS, {
                ...(this.props.location.state && this.props.location.state.scrollTo ? { scrollTo: this.props.location.state.scrollTo } : {}),
            });
        }
    }

    private isFromStoryViewerExit = (didSave: boolean, photo: IUserPhoto) => {
        if (didSave === false) {
            const photoToUpdate = this.props.store.userPhotoStore!.userPhotos.find(x => x.id === parseInt(this.props.match.params.id));
            if (photoToUpdate) {
                photoToUpdate.url = this.originalPhotoURL;
            }
        }

        this.props.history.push(
            Routes.PHOTO_VIEWER.replace(':id', this.props.location.state.fromStoryInPhotoViewer).replace(':photoID?', photo.id.toString()),
            {
                focusPhoto: parseInt(this.props.match.params.id),
            },
        );
    }

    private renderPhotoDataInput(tagType: TagType) {
        return (
            <PhotoDataInput
                tagType={tagType}
                image={this.state.photo!.description}
                onImageUpdate={description => {
                    const photo = { ...this.state.photo, description };
                    this.setState({ photo });
                }}
            />
        );
    }

    private async removePhotoFromStory(storyID: number): Promise<void> {
        await this.props.store.storyStore.removePhotoFromStory(parseInt(this.props.match.params.id), storyID);
        const storiesForPhoto = [...this.state.storiesForPhoto];
        const foundIndex = storiesForPhoto.findIndex(s => s.id === storyID);
        storiesForPhoto.splice(foundIndex, 1);
        this.setState({ storiesForPhoto, removeStoryIndex: undefined });
    }

    private renderCellForPhotoStories(story: IStoryFromServer, index: number) {
        return (
            <div style={styles.storyInfoContainer} key={index}>
                <Link to={Routes.PHOTO_VIEWER.replace(':id/:photoID', `${story.id.toString()}/${parseInt(this.props.match.params.id)}`)} style={styles.storyText}>
                    {story.title}
                </Link>
            </div>
        );
    }

    private async saveChangesToPhoto(): Promise<void> {
        this.setState({ isSavingPhoto: true });
        const photo = this.state.photo!;
        const rotation = (photo.rotate_degrees + this.state.newRotation) % 360;
        photo.rotate_degrees = rotation;

        await this.props.store.userPhotoStore.updatePhoto(photo, this.state.rotatedPhoto, this.state.rotatedThumbnail);
        await this.props.store.storyStore.getAllStories();

        // If the photo being edited is on anything other than the first infinite scroll page,
        // forget the stored scroll position. Scrolling back there is tricky since we're re-fetching photos.
        const pageSize = this.props.store.userPhotoStore.getAllPhotosForCurrentUserMeta.page_size;
        if (pageSize && this.state.photoIndex > pageSize) {
            this.props.store.userPhotoStore.setPhotosPageScrollTo(0);
        }

        // Clear the photos. They will be re-fetched next time the photos page loads.
        await this.props.store.userPhotoStore.clearUserPhotos();

        this.state.onPressExit(true);
    }

    private getMatchingPhoto(id: number) {
        const {allStories} = this.props.store.storyStore;

        let match = null;
        allStories.forEach(story => {
            story.photosWithComments.forEach(p => {
                if (p.photo.id === id) {
                    console.log('p.photo', p.photo);
                    match = p.photo;
                }
            });
        });

        return match as unknown as IUserPhoto;
    }

    public componentDidMount(): void {
        if (this.props.store.userPhotoStore && this.props.store.userPhotoStore.initialPhotosAreLoaded) {
            const photo = this.props.store.userPhotoStore.userPhotos.find(p => p.id === parseInt(this.props.match.params.id))!;
            this.setState({ photo });
        }

        when(
            () =>
                this.props.store.userPhotoStore &&
                // this.props.store.userPhotoStore.initialPhotosAreLoaded &&
                this.props.store.storyStore &&
                this.props.store.storyStore.storiesAreLoaded,
            async () => {

                const photoIndex = this.props.store.userPhotoStore.userPhotos.findIndex(p => p.id === parseInt(this.props.match.params.id))!;

                // Try getting the photo from the photos list first
                let photo = this.props.store.userPhotoStore.userPhotos.find(p => p.id === parseInt(this.props.match.params.id))!;

                // If it's not there, dig through the albums to find it.
                if (!photo) {
                    photo = this.getMatchingPhoto(parseInt(this.props.match.params.id));
                }

                const storiesForPhoto = await this.props.store.storyStore.getStoriesForPhotoID(photo.id);
                this.originalPhotoURL = photo.url;
                this.originalThumbnailURL = photo.thumbnail_url;

                this.setState({
                    photo,
                    photoIndex,
                    storiesForPhoto,
                    originalDate: photo.description.date,
                    originalLocation: photo.description.location,
                });

                if (this.props.location.state && this.props.location.state.fromStoryInPhotoViewer) {
                    this.setState({
                        onPressExit: (didSave: boolean) => {
                            this.isFromStoryViewerExit(didSave, photo);
                        },
                    });
                }
            },
        );
    }

    private rotatePhoto(): void {
        const photo = this.state.photo!;
        const rotation = getNextRotation(this.state.newRotation);
        this.hasRotated = true;
        //TODO: show loading state in rotation because it sometimes works slowly;
        rotateImage(this.originalPhotoURL, rotation)
            .then(([url, file]) => {
                rotateImage(this.originalThumbnailURL || this.originalPhotoURL, rotation).then(([thumbnail_url, thumbnail_file]) => {
                    photo.url = url;

                    this.setState({
                        photo,
                        rotatedPhoto: file,
                        rotatedThumbnail: thumbnail_file,
                        newRotation: rotation,
                    });
                });
            })
            .catch(() => null);
    }

    private saveIsEnabled(): boolean {
        if (this.state.photo === undefined || this.state.originalDate === undefined || this.state.originalLocation === undefined) {
            return false;
        }

        if (this.state.photo.tags === undefined) {
            return false;
        }

        const locationChanged = this.state.photo.description.location !== this.state.originalLocation;
        const dateChanged = this.state.photo.description.date !== this.state.originalDate;
        const hasRotated = this.hasRotated;
        return locationChanged || dateChanged || hasRotated;
    }

    public render() {
        if (this.state.photo === undefined) {
            return null;
        }

        return (
            <FullScreenModalContainer {...this.props} justifyContent="space-between" alignItems="center" onPressExit={() => this.state.onPressExit(false)}>
                <FullContainer style={styles.mainContainer}>
                    <div style={styles.leftContainer}>
                        <img
                            src={this.state.photo.url}
                            style={styles.imageContainer}
                            alt={this.state.photo.description.filename}
                        />
                    </div>
                    <div style={styles.rightContainer}>
                        <div>
                            <CommonTabbedContainer
                                leftHeader={'Facts'}
                                rightHeader={'Albums'}
                                activeTab={this.state.activeTab}
                                onPressTab={(tab: Tab) => this.setState({ activeTab: tab })}
                                leftComponent={() => (
                                    <div style={styles.inputColumn}>
                                        <div style={styles.inputsContainer}>
                                            {this.renderPhotoDataInput(TagType.LOCATION)}
                                            {this.renderPhotoDataInput(TagType.DATE)}
                                            {this.renderPhotoDataInput(TagType.FILENAME)}
                                        </div>
                                    </div>
                                )}
                                rightComponent={() => (
                                    <div>
                                        {this.state.storiesForPhoto.length === 0 && <p style={styles.photoNotInStory}>{ui.NOT_A_PART_OF_STORY}</p>}
                                        {this.state.storiesForPhoto.map((story, index) => {
                                            return this.renderCellForPhotoStories(story, index);
                                        })}
                                    </div>
                                )}
                                containerStyle={styles.tabContainer}
                                tabStyle={styles.tabText}
                            />
                            {this.state.activeTab === Tab.LEFT && (
                                <CommonActionButton containerStyle={styles.leftTabMargin} onClick={() => this.rotatePhoto()} buttonContent={ui.ROTATE} />
                            )}
                        </div>
                        {this.state.activeTab === Tab.LEFT && (
                            <ModalActionButtonBar
                                confirmOnClick={() => this.saveChangesToPhoto()}
                                confirmContent={ui.CONFIRM_TEXT}
                                style={styles.actionButtonText}
                                altPadding={25}
                                confirmDisabled={this.saveIsEnabled() === false}
                            />
                        )}
                    </div>

                    {this.state.removeStoryIndex && (
                        <RemovalConfirmationPopup
                            headerText={`${ui.ARE_YOU_SURE_REMOVE}: ${this.props.store.storyStore.getStoryByID(this.state.removeStoryIndex).title}?`}
                            subHeaderText={ui.CANNOT_BE_UNDONE}
                            onClickCancel={() => this.setState({ removeStoryIndex: undefined })}
                            onClickRemove={() => this.removePhotoFromStory(this.state.removeStoryIndex!)}
                        />
                    )}

                    {this.state.isSavingPhoto && <ExitableAlert headerText={'Saving updates to photo...'} />}
                </FullContainer>
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    leftTabMargin: {
        marginLeft: 25,
    },
    photoNotInStory: {
        fontSize: 18,
        textAlign: 'center',
        width: '100%',
        padding: 20,
        paddingTop: 85,
    } as React.CSSProperties,
    actionButtonText: {
        paddingLeft: 12,
        paddingRight: 12,
    },
    tabContainer: {
        height: 180,
        paddingTop: 110,
        borderBottom: '1px solid #E5E5E5',
        backgroundColor: '#F5F5F5',
    },
    tabText: {
        fontSize: 20,
    },
    leftContainer: {
        width: '63%',
        height: '100%',
        backgroundColor: '#EEEEEE',
    },
    imageContainer: {
        height: '100%',
        width: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
    } as React.CSSProperties,
    rightContainer: {
        width: '37%',
        height: '100%',
        backgroundColor: '#F5F5F5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    } as React.CSSProperties,
    inputColumn: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 35,
        paddingRight: 35
    } as React.CSSProperties,
    inputsContainer: {
        width: '90%',
    },
    inputContainer: {
        width: '100%',
        height: 85,
        backgroundColor: 'white',
    },
    input: (shouldDisable: boolean) => {
        return {
            width: '100%',
            height: 85,
            borderRadius: 5,
            boxShadow: '0px 10px 35px rgba(0, 0, 0, 0.08)',
            marginBottom: 25,
            opacity: shouldDisable ? 0.5 : 1,
        };
    },
    inputCancel: {
        cursor: 'pointer',
        height: 30,
        width: 30,
        borderRadius: 15,
        position: 'relative',
        right: '-89%',
        bottom: 72,
        backgroundColor: '#00000022',
    } as React.CSSProperties,
    cancelIcon: {
        height: '100%',
        width: '100%',
        objectFit: 'contain',
    },
    cancelButton: {
        width: 110,
        height: 35,
        backgroundColor: colors.ACTION_BAR,
    },
    buttonTextCancel: {
        color: colors.CANCEL_TEXT,
        fontSize: 14,
    },
    storyInfoContainer: {
        height: 70,
        display: 'flex',
        justifyContent: 'space-between',
        paddingLeft: 30,
        paddingRight: 25,
        borderBottom: '1px solid #48A29B30',
        alignItems: 'center',
    } as React.CSSProperties,
    flex: {
        display: 'flex',
    },
    mainContainer: {
        display: 'flex',
        position: 'relative',
    } as React.CSSProperties,
    storyText: {
        color: '#423F4E',
        fontSize: 20,
        cursor: 'pointer',
        width: '100%',
        height: 60,
        display: 'flex',
        alignItems: 'center',
    } as React.CSSProperties,
};



// WEBPACK FOOTER //
// ./src/Pages/PhotosPage/PhotoDetail.tsx